import type { ImageFitMode } from '@kontent-ai/delivery-sdk';
import type { ImageCompression } from '@kontent-ai/delivery-sdk/lib/images/image.models';

type Parameters = 'dpr' | 'fit' | 'h' | 'w';

type ImageFormat = 'gif' | 'png' | 'png8' | 'jpg' | 'pjpg' | 'webp';

type QueryParams = {
  dpr?: string;
  fit?: ImageFitMode;
  h?: string;
  w?: string;
  fm?: string;
  q?: string;
  lossless?: string;
  auto?: string;
};

export class ImageUrlTransformationBuilder {
  private queryParams: QueryParams = {};

  private readonly imageUrl: string;

  constructor(imageUrl: string) {
    this.imageUrl = imageUrl;
  }

  /**
   * The dpr transformation is used to serve correctly sized
   * images for devices that expose a device pixel ratio.
   * @param dpr A required DPR value.
   */
  withDpr(dpr: number): this {
    this.queryParams.dpr = dpr.toString(10);
    return this;
  }

  /**
   * Applies the quality parameter that enables control over the compression level for lossy file-formatted images.
   * @param quality The required quality of the image.
   */
  withQuality(quality: number): this {
    this.queryParams.q = quality.toString();
    return this;
  }

  /**
   * Enables WebP image support.
   * @param backupFormat
   */
  withAutomaticFormat(backupFormat?: ImageFormat): this {
    this.queryParams.auto = 'format';

    if (backupFormat) {
      this.withFormat(backupFormat);
    }

    return this;
  }

  withCompression(compression: ImageCompression): this {
    this.queryParams.lossless = compression === 'lossless' ? 'true' : 'false';
    return this;
  }

  /**
   * The fit transformation controls how the output image is fit to
   * its target dimensions after resizing.
   * @param fitMode Specifies the mode for the transformation.
   */
  withFitMode(fitMode: ImageFitMode): this {
    this.queryParams.fit = fitMode;
    return this;
  }

  /**
   * The height transformation enables dynamic height resizing based on pixels and percent values.
   * @param height A required image height.
   */
  withHeight(height: number): this {
    this.queryParams.h = height.toString(10);
    return this;
  }

  /**
   * The width transformation enables dynamic width resizing based on pixels and percent values.
   * @param width A required image width.
   */
  withWidth(width: number): this {
    this.queryParams.w = width.toString(10);
    return this;
  }

  /**
   * The format transformation enables use of different image formats.
   * @param format A required image format.
   */
  withFormat(format: ImageFormat): this {
    this.queryParams.fm = format;
    return this;
  }

  /**
   * Gets query string part of the image URL
   */
  getQueryString(): string {
    let query = this.imageUrl;
    Object.keys(this.queryParams).forEach((key: string): void => {
      const filter = this.queryParams[key as Parameters];
      if (query && query.indexOf('?') > -1) {
        query = `${query}&${key}=${filter ?? ''}`;
      } else {
        query = `${query}?${key}=${filter ?? ''}`;
      }
    });

    return query.replace(this.imageUrl, '').replace(/\?&+/g, '?').replace(/&+/g, '&');
  }

  /**
   * Gets full transformed url to image with parameters
   */
  getUrl(): string {
    return this.imageUrl + this.getQueryString();
  }
}
